'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startLiveSpeech, liveSpeechKind, type LiveSpeech, type LiveState,
} from '@/lib/liveSpeech';
import { fetchAudioLimits } from '@/lib/api';

/**
 * The mic, bottom-left of the input box, beside the voice-file button.
 *
 * ── What it owns ─────────────────────────────────────────────────────────────
 *
 * The engine's whole lifecycle, and nothing about the text. It reports words
 * upward as they arrive and lets the box decide where they go — the box is the
 * only thing that knows where the caret is, what else the user has typed since,
 * and whether the character limit has been reached. That split is why
 * dictating, typing and pasting can all happen in the same sentence without
 * fighting each other.
 *
 * ── Why it can disappear ─────────────────────────────────────────────────────
 *
 * Rendered only once the browser has been asked whether it can do this at all,
 * which cannot happen before mount without the server and the client disagreeing
 * about the first paint. A browser with neither speech recognition nor a
 * recorder gets no button rather than one that explains itself when pressed:
 * a control that exists only to refuse is worse than the absence it is standing
 * in for.
 */

export interface MicButtonProps {
  /**
   * BCP-47 tag to recognise.
   *
   * ⚠️ Empty does NOT mean "the browser's default" — it means nobody knows yet,
   * which routes the first phrase through our own transcription instead, the
   * one path that can hear what language it is. See `startLiveSpeech`.
   */
  lang: string;
  /** No room left in the box — dictating into a full box would be a no-op. */
  disabled?: boolean;
  /** Dictation is starting: anchor it wherever the caret is now. */
  onStart: () => void;
  /** A revisable guess. Replaces the previous partial. */
  onPartial: (text: string) => void;
  /** Committed words, to keep. */
  onFinal: (text: string) => void;
  /** Dictation ended, however it ended. Any partial on screen is now void. */
  onStop: () => void;
  /** A message to show, or null to clear the last one. */
  onError: (message: string | null) => void;
  /**
   * The language the voice turned out to be in, discovered from the audio.
   *
   * Only ever fires on the first phrase or two, and only when nobody had
   * declared a language: it is how dictation stops guessing. The caller feeds
   * it back in as `lang`, which hands the rest of the session to the free
   * engine.
   */
  onLanguage?: (code: string) => void;
}

export default function MicButton(props: MicButtonProps) {
  const [kind, setKind] = useState<'browser' | 'server' | 'none' | null>(null);
  const [state, setState] = useState<LiveState>('stopped');
  const [level, setLevel] = useState(0);

  const engine = useRef<LiveSpeech | null>(null);
  /**
   * The callbacks, held in a ref rather than closed over.
   *
   * The engine is started once and lives for the whole of a dictation, while
   * the parent re-renders on every partial word — so a handler captured at
   * start time would be writing into a stale copy of the box within a second.
   */
  const handlers = useRef(props);
  handlers.current = props;

  /**
   * Which engine's callbacks are still the current ones.
   *
   * Dictation can change engine halfway through — the first phrase reveals the
   * language, and the free word-by-word recogniser takes over from there. The
   * outgoing engine reports `stopped` on its way out, and that report must NOT
   * reach the box: `onStop` there means "the user has finished", which drops
   * the dictation anchor and asks for a translation. A handover is neither.
   *
   * So each start takes a ticket, and callbacks holding an old one are ignored.
   */
  const session = useRef(0);
  /** Which engine is running, so a failure can be blamed on the right one. */
  const running = useRef<'browser' | 'server' | null>(null);
  /**
   * The browser's own recogniser has proved unusable on this page.
   *
   * Set when it fails for a reason our own route would not share — its service
   * unreachable, refusing to start — and never unset. From then on dictation
   * goes through us instead: slower and not free, but the alternative is
   * telling someone their microphone is broken when it plainly is not.
   */
  const browserUnusable = useRef(false);

  const active = state !== 'stopped';

  /**
   * Whether our own transcription route will answer.
   *
   * Asked for on EVERY browser now, not just the ones with no recogniser of
   * their own. It used to be a Firefox-only question, but that route is now
   * also how any browser finds out what language is being spoken before it has
   * a language to recognise with — so Chrome depends on it too, for the first
   * phrase of a dictation that nobody has declared a language for.
   */
  const [serverLive, setServerLive] = useState<boolean | null>(null);
  /** The same answer, readable from `start`, which is not re-created per render. */
  const serverLiveRef = useRef<boolean | null>(null);
  serverLiveRef.current = serverLive;

  useEffect(() => {
    const detected = liveSpeechKind();
    setKind(detected);
    if (detected === 'none') { setServerLive(false); return; }
    let live = true;
    fetchAudioLimits()
      .then((limits) => { if (live) setServerLive(Boolean(limits.live?.enabled)); })
      .catch(() => { if (live) setServerLive(false); });
    return () => { live = false; };
  }, []);

  // A dictation must not outlive the page it was started on.
  useEffect(() => () => { engine.current?.stop(); engine.current = null; }, []);

  /**
   * Stop, and be certain the button comes back.
   *
   * The state is reset here rather than left to the engine's `stopped` event,
   * because of the case that had users stuck: a fatal error discards the engine
   * while the button is still lit, and a press then reached `engine.current?.
   * stop()` — an optional call on null, which does nothing at all. The control
   * looked live, did nothing when pressed, and only a page reload recovered it.
   *
   * A live engine still announces `stopped` itself, and is left to — the server
   * one stays on "Writing…" until the phrase that was in flight comes back, and
   * forcing the resting state here would throw that away. This covers only the
   * case where there is nothing left to do the announcing.
   */
  const stop = useCallback(() => {
    const running = engine.current;
    engine.current = null;
    if (running) { running.stop(); return; }
    setState('stopped');
    setLevel(0);
    handlers.current.onStop();
  }, []);

  /**
   * Open the microphone.
   *
   * `resume` is a handover rather than a fresh start: the box keeps the anchor
   * it already has, so the words that arrive next carry on where the last ones
   * left off instead of jumping to wherever the caret happens to be.
   */
  const start = useCallback(async ({ resume = false } = {}) => {
    const mine = ++session.current;
    const mineStill = () => session.current === mine;

    handlers.current.onError(null);
    if (!resume) handlers.current.onStart();
    setState('starting');

    const started = await startLiveSpeech({
      lang: handlers.current.lang,
      serverAvailable: serverLiveRef.current !== false,
      forceServer: browserUnusable.current,
      onPartial: (text) => { if (mineStill()) handlers.current.onPartial(text); },
      onFinal: (text) => { if (mineStill()) handlers.current.onFinal(text); },
      onLevel: (v) => { if (mineStill()) setLevel(v); },
      onLanguage: (code) => { if (mineStill()) handlers.current.onLanguage?.(code); },
      onState: (next) => {
        if (!mineStill()) return;
        setState(next);
        if (next === 'stopped') {
          engine.current = null;
          handlers.current.onStop();
        }
      },
      onError: (message, fatal, cause) => {
        if (!mineStill()) return;

        /*
         * The browser's recogniser has failed for a reason ours would not
         * share. Change engine instead of reporting it.
         *
         * Worth the extra branch because the failure is common and invisible:
         * Chrome's speech recognition is a network service, and on a flaky
         * connection or a restricted network it simply refuses. The user did
         * nothing wrong, their microphone is fine, and we have another way to
         * do the job — so the honest response is to use it, not to hand them an
         * error about a thing they cannot fix.
         *
         * `permission` and `device` deliberately do NOT come here: both engines
         * need the same microphone, so retrying would fail identically and the
         * user does need to hear about those.
         */
        if (fatal && cause === 'service' && running.current === 'browser' && serverLiveRef.current) {
          browserUnusable.current = true;
          handlers.current.onError(null);
          session.current++;
          engine.current = null;
          running.current = null;
          setTimeout(() => { void start({ resume: true }); }, 60);
          return;
        }

        handlers.current.onError(message);
        // The engines all announce `stopped` alongside a fatal error, but this
        // does not wait to be told: a button left lit over a closed microphone
        // is the one wrong state that cannot be recovered from by pressing it.
        // `onStop` tolerates being called twice — the second finds no anchor.
        if (fatal) {
          engine.current = null;
          running.current = null;
          setState('stopped');
          handlers.current.onStop();
        }
      },
    });

    // Superseded while the microphone was opening — a language arrived, or the
    // user pressed stop. Whatever came up is not wanted; close it again.
    if (!mineStill()) { started?.stop(); return; }
    // Null means the engine refused before it began and has already reported
    // why; `onState` never fired, so the resting state has to be restored here.
    if (!started) { setState('stopped'); handlers.current.onStop(); return; }
    engine.current = started;
    running.current = started.kind;
  }, []);

  /**
   * The handover: the language changed under a running dictation, so the engine
   * changes with it.
   *
   * Two things arrive this way, and both need it. The user picks a source
   * language, or — the ordinary case now — the first phrase came back from our
   * own route with the language attached, and the free word-by-word recogniser
   * can take over for the rest of the session. Either way the engine cannot be
   * retuned in place: `recognition.lang` is fixed for the life of a session,
   * and the wrong one does not degrade gracefully.
   *
   * Everything already in the box stays there. The ticket is taken FIRST, so
   * the outgoing engine's `stopped` is ignored rather than reaching the box as
   * "the user has finished" — and the new one resumes onto the same anchor
   * instead of re-reading the caret.
   */
  useEffect(() => {
    if (!active) return;
    // Nothing to hand over to. Without a recogniser of its own this browser
    // stays on our route for the whole dictation, and that route neither takes
    // a language nor needs one.
    if (kind !== 'browser') return;

    const outgoing = engine.current;
    session.current++;
    engine.current = null;
    outgoing?.stop();

    const id = setTimeout(() => { void start({ resume: true }); }, 60);
    return () => clearTimeout(id);
    // Deliberately keyed on the language alone. `active` is not a dependency:
    // listing it would make this run on every start and stop, restarting the
    // engine it has just been told about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.lang]);

  if (kind === null || kind === 'none') return null;

  const label =
    state === 'starting' ? 'Starting…'
      : state === 'thinking' ? 'Writing…'
        : state === 'listening' ? 'Listening'
          : 'Speak';

  /*
    The tooltip is the only place the spoken commands are documented, and it is
    on the resting button rather than the live one on purpose: while dictation
    is running the pointer is nowhere near this, and the moment to learn that
    "new line" breaks the line is before pressing it, not after saying it.

    No language is named. It used to say "Speak in Korean", which was a promise
    about the recogniser's setting — and the setting is now worked out from the
    voice rather than declared, so naming one would be stating a guess as a
    fact. Any language is the honest answer.
  */
  const title = props.disabled
    ? 'The box is full'
    : active
      ? 'Stop dictating'
      : 'Speak in any language and it appears in the box.\n'
        + 'Say “new line” or “enter” on its own to break the line.';

  return (
    <>
    <button
      type="button"
      onClick={() => (active ? stop() : void start())}
      disabled={props.disabled && !active}
      title={title}
      aria-label={active ? 'Stop dictating' : 'Dictate with the microphone'}
      aria-pressed={active}
      className={
        'relative flex shrink-0 items-center gap-1.5 overflow-hidden rounded-full border px-2.5 py-1 ' +
        'text-[11px] font-medium transition disabled:opacity-40 ' +
        (active
          // Red while live, and only while live. It is the one control on this
          // page that is doing something to the room the user is sitting in,
          // and it has to be unmistakable at a glance which of those two states
          // it is in — every other colour here reads as decoration.
          ? 'border-red-500/70 bg-red-500/10 text-red-600 dark:border-red-400/60 dark:text-red-400'
          : 'border-ink-200 text-ink-500 hover:border-ink-300 hover:bg-ink-100 hover:text-ink-800 ' +
            'dark:border-ink-700 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100')
      }
    >
      {/*
        The level, painted BEHIND the label rather than beside it.

        A separate meter would need its own width in a row that is already
        holding two buttons and a character count on a phone. As a backdrop it
        costs no layout at all, and it answers the question people actually ask
        of a microphone — "can it hear me?" — more directly than a number would.

        On the browser engine there is no real level to show (see liveSpeech.ts);
        what breathes here is recognition activity, which answers the same
        question from the other end: words are getting through.
      */}
      {active && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 bg-red-500/25 transition-[width] duration-100 dark:bg-red-400/25"
          style={{ width: `${Math.round(level * 100)}%` }}
        />
      )}
      <span className="relative flex items-center gap-1.5">
        {active ? (
          // A square, not a mic: while it is running the button's job is to
          // stop, and the universal shape for that is not a microphone.
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
            <rect x="6" y="6" width="12" height="12" rx="2.5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0014 0M12 18v3" />
          </svg>
        )}
        {label}
      </span>
    </button>
    </>
  );
}
